/**
 * Message protocol for the local-multiplayer DataChannel.
 *
 * JSON versioned and VALIDATED on receipt: a peer never trusts the wire
 * (truncated messages, future versions, garbage). `decodeDuoMessage`
 * returns `null` for anything it does not understand — the caller discards it
 * silently (without logging payloads).
 *
 * Directions:
 * - guest → host: `hello` (when the channel opens), `move` (intent), `again`
 *   (rematch intent), `bye`.
 * - host → guest: `welcome` (accepts the hello), `state` (full state after
 *   every change — authority broadcast), `bye`.
 */

import { type DuoState, isDuoState, isRecord } from "./duo-tictactoe";

export const DUO_PROTOCOL_VERSION = 1;

export type DuoMessage =
  | { t: "hello"; v: number }
  | { t: "welcome"; v: number }
  | { t: "state"; v: number; state: DuoState }
  | { t: "move"; v: number; cell: number }
  | { t: "again"; v: number }
  | { t: "bye"; v: number };

export function encodeDuoMessage(message: DuoMessage): string {
  return JSON.stringify(message);
}

/** Parses and validates a channel message; `null` = discard. */
export function decodeDuoMessage(raw: unknown): DuoMessage | null {
  if (typeof raw !== "string" || raw.length > 4096) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  const m = parsed;
  if (m.v !== DUO_PROTOCOL_VERSION) {
    return null;
  }
  switch (m.t) {
    case "hello":
    case "welcome":
    case "again":
    case "bye": {
      return { t: m.t, v: DUO_PROTOCOL_VERSION };
    }
    case "move": {
      if (typeof m.cell === "number" && Number.isInteger(m.cell) && m.cell >= 0 && m.cell <= 8) {
        return { t: "move", v: DUO_PROTOCOL_VERSION, cell: m.cell };
      }
      return null;
    }
    case "state": {
      if (isDuoState(m.state)) {
        return { t: "state", v: DUO_PROTOCOL_VERSION, state: m.state };
      }
      return null;
    }
    default: {
      return null;
    }
  }
}
